import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'utils/classMerge'

import Text from '../text'
import { LoadingSpinner } from '../loading/spinner'

const buttonVariants = cva(
  'focus-visible:tw-ring-ring tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-font-medium tw-leading-[1] tw-transition-colors tw-duration-200 tw-ease-in-out focus-visible:tw-outline-none focus-visible:tw-ring-1 disabled:tw-pointer-events-none disabled:tw-cursor-not-allowed disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0',
  {
    variants: {
      variant: {
        'primary-solid':
          'tw-border-0 tw-bg-orange-500 tw-text-white tw-shadow-sm hover:tw-bg-orange-600',
        'primary-outlined':
          'tw-border tw-border-solid tw-border-orange-500 tw-bg-transparent tw-text-orange-500 hover:tw-border-orange-600 hover:tw-text-orange-600 active:tw-border-orange-600 active:tw-text-orange-600 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        'primary-link':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-orange-500 hover:tw-text-orange-600',
        'primary-link-underline':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-orange-500 tw-underline tw-decoration-orange-500/25 tw-underline-offset-4 hover:tw-text-orange-600 hover:tw-decoration-orange-600',
        'accent-solid':
          'tw-border-0 tw-bg-blue-500 tw-text-white tw-shadow-sm hover:tw-bg-blue-600',
        'accent-outlined':
          'tw-border tw-border-solid tw-border-blue-500 tw-bg-transparent tw-text-blue-500 hover:tw-border-blue-600 hover:tw-text-blue-600 active:tw-border-blue-600 active:tw-text-blue-600 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        'accent-link':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-blue-500 hover:tw-text-blue-600',
        'accent-link-underline':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-blue-500 tw-underline tw-decoration-blue-500/25 tw-underline-offset-4 hover:tw-text-blue-600 hover:tw-decoration-blue-600',
        'neutral-solid':
          'tw-border tw-border-solid tw-border-zinc-300 tw-bg-white tw-text-gray-900 tw-shadow-sm hover:tw-bg-gray-50',
        'neutral-outlined':
          'tw-border tw-border-solid tw-border-zinc-300 tw-bg-transparent tw-text-zinc-600 hover:tw-border-zinc-400 hover:tw-text-zinc-900 active:tw-border-zinc-400 active:tw-text-zinc-900 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        'neutral-link':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-zinc-600 hover:tw-text-zinc-900',
        'neutral-link-underline':
          'tw-h-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-zinc-600 tw-underline tw-decoration-zinc-600/25 tw-underline-offset-4 hover:tw-text-zinc-900 hover:tw-decoration-zinc-900',
        // Legacy variants for backward compatibility
        /** @deprecated Use 'accent-solid' instead */
        'legacy-primary':
          'tw-w-fit tw-rounded tw-border-none tw-bg-blue-500 tw-px-10 tw-py-4 tw-text-sm tw-font-medium tw-uppercase tw-tracking-[1.12px] tw-text-white',
        /** @deprecated Use 'accent-link-underline' instead */
        'legacy-link':
          'tw-border-none tw-bg-white tw-uppercase tw-text-blue-500 tw-underline',
        /** @deprecated Use 'neutral-outlined' instead */
        'legacy-box':
          'tw-font-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-whitespace-pre-wrap tw-rounded-none tw-border tw-border-solid tw-border-zinc-200 tw-bg-white tw-p-4 hover:tw-border-orange-500 md:tw-flex-row',
        /** @deprecated Use 'primary-outlined' instead */
        'legacy-reload':
          'tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-solid tw-border-orange-500 tw-bg-white tw-px-4 tw-py-2 tw-uppercase',
        /** @deprecated Use 'primary-solid' instead */
        'legacy-main':
          'tw-m-0 tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-sm tw-border tw-border-solid tw-border-white tw-bg-orange-500 tw-py-0 tw-font-roboto tw-text-sm tw-font-bold tw-uppercase tw-tracking-[0.1px] tw-text-white',
        /** @deprecated Use 'neutral-outlined' instead */
        'legacy-neutralOutline':
          'tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-zinc-300 tw-bg-transparent tw-text-zinc-600 hover:tw-border-zinc-400 hover:tw-text-zinc-900 active:tw-border-zinc-400 active:tw-text-zinc-900 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        /** @deprecated Use 'neutral-outlined' instead */
        'legacy-outlined':
          'tw-font-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-whitespace-pre-wrap tw-rounded-md tw-border tw-border-solid tw-border-zinc-200 tw-bg-white tw-px-4 tw-text-base tw-font-medium hover:tw-border-orange-500 md:tw-flex-row',
        /** @deprecated Use 'neutral-link' instead */
        'legacy-onlyText':
          'tw-border-none tw-bg-white tw-text-base tw-text-sm tw-capitalize tw-text-zinc-600 tw-no-underline',
      },
      size: {
        default: 'tw-px-3 tw-py-2 tw-text-sm tw-font-medium',
        xs: 'tw-px-2 tw-py-1 tw-text-xs tw-font-medium',
        sm: 'tw-px-2 tw-py-1 tw-text-sm tw-font-medium',
        lg: 'tw-px-4 tw-py-2 tw-text-base tw-font-medium',
        legacy: '',
      },
      state: {
        default: '',
        active: 'tw-bg-orange-600',
        disabled: 'tw-cursor-not-allowed tw-opacity-50',
      },
    },
    defaultVariants: {
      variant: 'primary-solid',
      size: 'default',
      state: 'default',
    },
  },
)

const commonLegacyStyles = 'tw-rounded-none'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      state,
      asChild = false,
      isLoading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    // When using asChild (polymorphic component), we pass all props directly to the child
    // component via Slot and avoid wrapping children with Text to prevent React.Children.only errors
    if (asChild) {
      const isLegacyVariant = variant && variant.toString().includes('legacy')
      return (
        <Comp
          className={cn(
            buttonVariants({ variant, size, state, className }),
            isLegacyVariant && commonLegacyStyles,
          )}
          ref={ref}
          disabled={disabled || isLoading}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, state, className }),
          variant &&
            variant.toString().includes('legacy') &&
            commonLegacyStyles,
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <LoadingSpinner
            width={16}
            height={16}
            className="tw-relative tw-z-10"
            color="currentColor"
          />
        )}
        <Text
          as="div"
          className="tw-relative tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-2"
          {...(typeof children === 'string'
            ? { skipTranslation: true }
            : { renderChildren: true })}
        >
          {children}
        </Text>
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export default Button
export { buttonVariants }
